/** /org/clientes/:clientId — cuántos trabajan en el cliente (todos lo ven) y quiénes (según scope). */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Users, CheckCircle2, Briefcase } from 'lucide-react';
import { StatCard } from '@/components/ui/stat-card';
import { EmptyState, LoaderBlock } from '@/components/internal/shared';
import { useOrgClient, useOrgPeople } from '@/hooks/useOrg';
import { OrgAssignmentsTable, OrgPersonLink } from '@/components/org/org-ui';
import { solutionName } from '@/components/org/org-format';

export function OrgClientPage() {
  const { t } = useTranslation();
  const { clientId } = useParams<{ clientId: string }>();
  const q = useOrgClient(clientId);
  const people = useOrgPeople();
  const nameByEmail = useMemo(() => new Map((people.data ?? []).map((p) => [p.email, p.full_name])), [people.data]);
  if (q.isLoading) return <LoaderBlock />;
  if (q.isError && q.error.data?.code !== 'NOT_FOUND') return <EmptyState title={t('org.home.error', 'No se pudo cargar el Org Service. Reintentá en unos segundos.')} />;
  if (q.isError || !q.data) return <EmptyState title={t('org.client.not_found', 'Ese cliente no está en el Org Service.')} />;
  const { client, peopleCount, confirmedCount, solutions, assignments, assignmentsScope } = q.data;
  const nameOf = (email: string) => nameByEmail.get(email) ?? email;
  const meta = [
    client.pod ? t('org.client.pod', 'Pod {{pod}}', { pod: client.pod }) : null,
    client.lead_email ? <span key="lead">{t('org.client.lead', 'Lead')}: <OrgPersonLink email={client.lead_email} name={nameOf(client.lead_email)} /></span> : null,
    client.growth_partner_email ? <span key="gp">{t('org.client.gp', 'Growth partner')}: <OrgPersonLink email={client.growth_partner_email} name={nameOf(client.growth_partner_email)} /></span> : null,
  ].filter(Boolean);
  return (
    <div className="space-y-4 pb-24">
      <Link to="/org?tab=clients" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline"><ArrowLeft className="h-4 w-4" />{t('org.client.back', 'Clientes')}</Link>
      <div>
        <h1 className="text-2xl font-bold">{client.display_name}</h1>
        {meta.length > 0 && <p className="flex flex-wrap gap-3 text-sm text-muted-foreground">{meta.map((m, i) => <span key={i}>{m}</span>)}</p>}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label={t('org.client.people', 'Personas asignadas')} value={peopleCount} icon={Users} tone="blue" size="sm" />
        <StatCard label={t('org.client.confirmed', 'Confirmadas por su manager')} value={confirmedCount} icon={CheckCircle2} tone="green" size="sm" />
        <StatCard label={t('org.client.solutions', 'Soluciones')} value={solutions.length} sub={solutions.map(solutionName).join(', ')} icon={Briefcase} tone="purple" size="sm" />
      </div>
      <div>
        <h3 className="mb-2 text-sm font-semibold">{t('org.client.who', 'Quién trabaja acá')}</h3>
        {assignmentsScope !== 'all' && <p className="mb-2 text-xs text-muted-foreground">{t(`org.solution.scope_${assignmentsScope}`, assignmentsScope)}</p>}
        <OrgAssignmentsTable rows={assignments} hide={['client']} />
      </div>
    </div>
  );
}
